import { UtilCsv } from '../../util/UtilCsv';
import type { VoucherRow } from './voucherTypes';
import { drawCode128, drawQr } from './voucherMarks';

export const openPdfFromBase64 = (encoded: string) => {
  const pdfData = atob(encoded);
  const blob = new Blob([UintArrayFromBinary(pdfData)], {
    type: 'application/pdf',
  });
  window.open(URL.createObjectURL(blob));
};

const UintArrayFromBinary = (binary: string) =>
  Uint8Array.from(binary, (c) => c.charCodeAt(0));

export const downloadTextFile = (text: string, filename: string, mime = 'text/csv') => {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export type VoucherImageCompany = {
  name: string;
  branch?: string;
  department?: string;
  logo?: string;
  accent?: string;
};

export type VoucherImageLabels = {
  type: string;
  issued: string;
  expiry: string;
  status: string;
  amount: string;
  issuedLabel: string;
  expiryLabel: string;
  issueeLabel: string;
  issuerLabel: string;
  generatedLabel: string;
  voucherLabel: string;
  presentHint: string;
};

const loadImage = (src?: string): Promise<HTMLImageElement | null> => {
  const url = String(src || '').trim();
  if (!url) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

const fitText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  fontWeight: string,
  maxSize: number,
  minSize: number,
  family: string,
) => {
  let size = maxSize;
  while (size > minSize) {
    ctx.font = `${fontWeight} ${size}px ${family}`;
    if (ctx.measureText(text).width <= maxWidth) return size;
    size -= 1;
  }
  ctx.font = `${fontWeight} ${minSize}px ${family}`;
  return minSize;
};

const drawContainedImage = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  box: number,
) => {
  const ratio = Math.min(box / img.width, box / img.height);
  const w = img.width * ratio;
  const h = img.height * ratio;
  ctx.drawImage(img, x + (box - w) / 2, y + (box - h) / 2, w, h);
};

const statusAccent = (status: string) => {
  if (status === 'ACTIVE') return '#15803d';
  if (status === 'REVOKED') return '#b91c1c';
  if (status === 'EXPIRED') return '#b45309';
  return '#475569';
};

export const downloadVoucherImage = async (
  row: VoucherRow,
  labels: VoucherImageLabels,
  company: VoucherImageCompany,
) => {
  const canvas = document.createElement('canvas');
  const width = 720;
  const height = 780;
  const scale = 2;
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(scale, scale);

  const accent = company.accent || '#0f766e';
  const companyName = company.name || labels.voucherLabel;
  const logo = await loadImage(company.logo);
  const generatedAt = new Date().toLocaleString();
  const code = String(row.code || '');

  ctx.fillStyle = '#111827';
  ctx.fillRect(0, 0, width, height);

  const cardX = 18;
  const cardY = 18;
  const cardW = width - 36;
  const cardH = height - 36;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 6;
  ctx.fillStyle = '#fffdf8';
  roundRect(ctx, cardX, cardY, cardW, cardH, 16);
  ctx.fill();
  ctx.restore();

  ctx.save();
  roundRect(ctx, cardX, cardY, cardW, cardH, 16);
  ctx.clip();

  ctx.fillStyle = accent;
  ctx.fillRect(cardX, cardY, cardW, 92);
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.beginPath();
  ctx.arc(cardX + cardW + 10, cardY - 8, 70, 0, Math.PI * 2);
  ctx.fill();

  const logoBox = 58;
  const logoX = cardX + 20;
  const logoY = cardY + 17;
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, logoX, logoY, logoBox, logoBox, 12);
  ctx.fill();
  if (logo) {
    drawContainedImage(ctx, logo, logoX + 5, logoY + 5, logoBox - 10);
  } else {
    ctx.fillStyle = accent;
    ctx.font = '700 26px Georgia, "Times New Roman", serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((companyName[0] || 'V').toUpperCase(), logoX + logoBox / 2, logoY + logoBox / 2 + 1);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  const headerTextX = logoX + logoBox + 14;
  ctx.fillStyle = '#ffffff';
  fitText(ctx, companyName, cardW - 160, '700', 24, 15, 'Georgia, "Times New Roman", serif');
  ctx.fillText(companyName, headerTextX, cardY + 42);
  const infoLine = [company.department, company.branch].filter(Boolean).join('  ·  ');
  ctx.font = '500 14px "Segoe UI", system-ui, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.88)';
  if (infoLine) ctx.fillText(infoLine, headerTextX, cardY + 66);

  const bodyX = cardX + 24;
  const bodyW = cardW - 48;
  const centerX = bodyX + bodyW / 2;
  let y = cardY + 122;

  const badge = `${labels.type}  ·  ${labels.status}`;
  ctx.font = '700 15px "Segoe UI", system-ui, sans-serif';
  const badgeW = Math.min(bodyW, ctx.measureText(badge).width + 24);
  ctx.fillStyle = `${statusAccent(String(row.status || 'ACTIVE'))}18`;
  roundRect(ctx, centerX - badgeW / 2, y - 18, badgeW, 28, 14);
  ctx.fill();
  ctx.fillStyle = statusAccent(String(row.status || 'ACTIVE'));
  ctx.textAlign = 'center';
  ctx.fillText(badge, centerX, y);
  ctx.textAlign = 'left';

  y += 26;
  const codeBoxH = 70;
  ctx.fillStyle = '#f4efe4';
  roundRect(ctx, bodyX, y, bodyW, codeBoxH, 14);
  ctx.fill();
  ctx.setLineDash([6, 5]);
  ctx.strokeStyle = accent;
  ctx.lineWidth = 1.4;
  roundRect(ctx, bodyX + 7, y + 7, bodyW - 14, codeBoxH - 14, 10);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = '#0f172a';
  fitText(ctx, code, bodyW - 32, '700', 36, 18, 'ui-monospace, "Cascadia Mono", "Segoe UI Mono", monospace');
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(code, centerX, y + codeBoxH / 2 + 1);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  y += codeBoxH + 38;
  ctx.fillStyle = accent;
  ctx.textAlign = 'center';
  fitText(ctx, labels.amount, bodyW, '700', 42, 24, 'Georgia, "Times New Roman", serif');
  ctx.fillText(labels.amount, centerX, y);
  ctx.textAlign = 'left';

  y += 30;
  const colW = bodyW / 2;
  const meta = [
    { label: labels.issuerLabel, value: row.issuer || '—' },
    { label: labels.issueeLabel, value: row.issuee || '—' },
    { label: labels.issuedLabel, value: labels.issued },
    { label: labels.expiryLabel, value: labels.expiry },
  ];
  meta.forEach((item, index) => {
    const x = bodyX + colW * (index % 2);
    const rowY = y + Math.floor(index / 2) * 50;
    ctx.fillStyle = '#64748b';
    ctx.font = '700 12px "Segoe UI", system-ui, sans-serif';
    ctx.fillText(item.label.toUpperCase(), x, rowY);
    ctx.fillStyle = '#1e293b';
    ctx.font = '700 18px "Segoe UI", system-ui, sans-serif';
    const value = String(item.value || '—');
    const clipped = value.length > 22 ? `${value.slice(0, 21)}…` : value;
    ctx.fillText(clipped, x, rowY + 22);
  });

  y += 92;
  const qrSize = 225;
  const qrX = centerX - qrSize / 2;
  drawQr(ctx, code, qrX, y, qrSize, '#0f172a', '#ffffff', logo);

  y += qrSize + 10;
  const barW = Math.min(360, bodyW - 40);
  const barH = 46;
  const barX = centerX - barW / 2;
  drawCode128(ctx, code, barX, y, barW, barH);
  ctx.fillStyle = '#475569';
  ctx.font = '700 13px ui-monospace, "Segoe UI Mono", monospace';
  ctx.textAlign = 'center';
  ctx.fillText(code, centerX, y + barH + 18);
  ctx.textAlign = 'left';

  ctx.restore();

  ctx.save();
  roundRect(ctx, cardX, cardY, cardW, cardH, 16);
  ctx.clip();
  ctx.fillStyle = accent;
  ctx.fillRect(cardX, cardY + cardH - 56, cardW, 56);
  ctx.fillStyle = 'rgba(255,255,255,0.96)';
  ctx.font = '500 13px "Segoe UI", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(labels.presentHint, cardX + cardW / 2, cardY + cardH - 34);
  ctx.font = '700 13px "Segoe UI", system-ui, sans-serif';
  ctx.fillText(`${labels.generatedLabel} ${generatedAt}  ·  ${labels.status}`, cardX + cardW / 2, cardY + cardH - 14);
  ctx.restore();

  const url = canvas.toDataURL('image/png');
  const link = document.createElement('a');
  link.href = url;
  link.download = `voucher-${row.code}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const exportRowsCsv = (rows: VoucherRow[], filename: string) => {
  UtilCsv.handleExportData(
    rows.map((row) => ({
      code: row.code,
      type: row.type,
      status: row.status || '',
      issuer: row.issuer,
      issuee: row.issuee || '',
      amount: row.amount,
      issued: row.issued,
      expiry: row.expiry || '',
      user: row.user || '',
      description: row.description || '',
    })),
    filename,
  );
};
